/**
 * PopririTab - Gestiune popriri angajati
 * Sub-tab-uri: Gestiune Popriri | Alocare Lunara | Export CSV
 */
import { useState, useEffect, useCallback, memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Settings, Plus, Trash2, Save, Loader2, ChevronLeft, ChevronRight, ChevronDown, Download,
  Edit, AlertTriangle, CheckCircle2, FileText, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { isActiveEmployee } from '../constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS_RO = [
  { value: 1, label: 'Ianuarie' }, { value: 2, label: 'Februarie' }, { value: 3, label: 'Martie' },
  { value: 4, label: 'Aprilie' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Iunie' },
  { value: 7, label: 'Iulie' }, { value: 8, label: 'August' }, { value: 9, label: 'Septembrie' },
  { value: 10, label: 'Octombrie' }, { value: 11, label: 'Noiembrie' }, { value: 12, label: 'Decembrie' }
];

const formatNum = (n) => {
  if (!n && n !== 0) return '-';
  return new Intl.NumberFormat('ro-RO').format(n);
};

const formatLuna = (luna) => {
  if (!luna) return '';
  const [y, m] = luna.split('-');
  return `${MONTHS_RO[parseInt(m) - 1]?.label || m} ${y}`;
};

const LunaNav = memo(function LunaNav({ month, year, onMonthChange, onYearChange }) {
  const handlePrev = () => { if (month === 1) { onMonthChange(12); onYearChange(year - 1); } else onMonthChange(month - 1); };
  const handleNext = () => { if (month === 12) { onMonthChange(1); onYearChange(year + 1); } else onMonthChange(month + 1); };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="outline" size="sm" onClick={handlePrev}><ChevronLeft className="w-4 h-4" /></Button>
      <div className="flex items-center gap-1 px-3 py-1.5 bg-slate-50 rounded-lg border">
        <Select value={String(month)} onValueChange={(v) => onMonthChange(parseInt(v))}>
          <SelectTrigger className="w-[120px] h-8 border-0 bg-transparent text-sm font-medium"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS_RO.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => onYearChange(parseInt(v))}>
          <SelectTrigger className="w-[80px] h-8 border-0 bg-transparent text-sm font-medium"><SelectValue /></SelectTrigger>
          <SelectContent>{[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button variant="outline" size="sm" onClick={handleNext}><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );
});

// ============ Gestiune Popriri ============
const GestiunePopriri = memo(function GestiunePopriri({ token }) {
  const [popriri, setPopriri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ employee_id: '', employee_name: '', suma_totala: '', infintator: '', iban_tert: '', banca_beneficiar: '', cif_beneficiar: '', detalii_1: '', detalii_2: '', detalii_3: '', detalii_4: '', urgent: '', plata_buget: '', descriere: '' });

  const fetchPopriri = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/popriri/list`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPopriri(d.popriri || []); }
    } finally { setLoading(false); }
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setEmployees((d.users || d || []).filter(u => u.role !== 'admin' && isActiveEmployee(u))); }
    } catch {}
  }, [token]);

  useEffect(() => { fetchPopriri(); fetchEmployees(); }, [fetchPopriri, fetchEmployees]);

  const openCreate = () => {
    setEditId(null);
    setForm({ employee_id: '', employee_name: '', suma_totala: '', infintator: '', iban_tert: '', banca_beneficiar: '', cif_beneficiar: '', detalii_1: '', detalii_2: '', detalii_3: '', detalii_4: '', urgent: '', plata_buget: '', descriere: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ employee_id: p.employee_id, employee_name: p.employee_name, suma_totala: String(p.suma_totala), infintator: p.infintator, iban_tert: p.iban_tert, banca_beneficiar: p.banca_beneficiar || '', cif_beneficiar: p.cif_beneficiar || '', detalii_1: p.detalii_1 || '', detalii_2: p.detalii_2 || '', detalii_3: p.detalii_3 || '', detalii_4: p.detalii_4 || '', urgent: p.urgent || '', plata_buget: p.plata_buget || '', descriere: p.descriere || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.employee_id || !form.suma_totala || !form.infintator || !form.iban_tert) {
      toast.error('Completați toate câmpurile obligatorii'); return;
    }
    try {
      const url = editId ? `${API_URL}/api/popriri/${editId}` : `${API_URL}/api/popriri/create`;
      const method = editId ? 'PUT' : 'POST';
      const body = editId
        ? { suma_totala: parseFloat(form.suma_totala), infintator: form.infintator, iban_tert: form.iban_tert, banca_beneficiar: form.banca_beneficiar, cif_beneficiar: form.cif_beneficiar, detalii_1: form.detalii_1, detalii_2: form.detalii_2, detalii_3: form.detalii_3, detalii_4: form.detalii_4, urgent: form.urgent, plata_buget: form.plata_buget, descriere: form.descriere }
        : { ...form, suma_totala: parseFloat(form.suma_totala) };
      const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (res.ok) { toast.success(editId ? 'Poprire actualizată' : 'Poprire creată'); setDialogOpen(false); fetchPopriri(); }
      else { const e = await res.json(); toast.error(e.detail || 'Eroare'); }
    } catch { toast.error('Eroare de rețea'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți această poprire și toate alocările aferente?')) return;
    try {
      const res = await fetch(`${API_URL}/api/popriri/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Poprire ștearsă'); fetchPopriri(); }
    } catch { toast.error('Eroare'); }
  };

  const handleEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.id === empId);
    setForm(f => ({ ...f, employee_id: empId, employee_name: emp ? `${emp.nume || ''} ${emp.prenume || ''}`.trim() : '' }));
  };

  // Group by employee
  const grouped = {};
  popriri.forEach(p => {
    if (!grouped[p.employee_id]) grouped[p.employee_id] = { name: p.employee_name, items: [] };
    grouped[p.employee_id].items.push(p);
  });

  return (
    <div className="space-y-4" data-testid="gestiune-popriri">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-600">Popriri active: {popriri.filter(p => p.status === 'activa').length} / {popriri.length}</h3>
        <Button size="sm" onClick={openCreate} data-testid="add-poprire-btn"><Plus className="w-4 h-4 mr-1" />Adaugă poprire</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : popriri.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-slate-50">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Nu există popriri. Adăugați prima poprire.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Table header */}
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_80px] gap-2 px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b">
            <span>Angajat / Înființator</span>
            <span className="text-right">Suma totală</span>
            <span className="text-right">Achitat</span>
            <span className="text-right">Sold rămas</span>
            <span className="text-center">Acțiuni</span>
          </div>
          {popriri.map(p => {
            const isExp = expandedId === p.id;
            const istoric = p.istoric || [];
            return (
              <div key={p.id} className="border-b last:border-0" data-testid={`poprire-${p.id}`}>
                <div
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_80px] gap-2 px-3 py-2 items-center hover:bg-slate-50 cursor-pointer"
                  onClick={() => setExpandedId(isExp ? null : p.id)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronDown className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${isExp ? '' : '-rotate-90'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.employee_name}</p>
                      <p className="text-xs text-slate-500 truncate">{p.infintator}</p>
                    </div>
                  </div>
                  <span className="text-sm text-right tabular-nums">{formatNum(p.suma_totala)}</span>
                  <span className="text-sm text-right tabular-nums">{formatNum(p.total_alocat)}</span>
                  <div className="text-right">
                    <span className={`text-sm font-semibold tabular-nums ${p.status === 'achitata' ? 'text-green-600' : ''}`}>
                      {formatNum(p.sold_ramas)}
                    </span>
                    {p.status === 'achitata' && <Badge className="ml-1 bg-green-100 text-green-700 hover:bg-green-100 text-[10px] px-1">Achitată</Badge>}
                  </div>
                  <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(p)}><Edit className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDelete(p.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {isExp && (
                  <div className="px-8 pb-3">
                    <p className="text-[10px] text-slate-400 font-mono mb-2">IBAN: {p.iban_tert}</p>
                    {istoric.length === 0 ? (
                      <p className="text-xs text-slate-400">Nu există plăți înregistrate</p>
                    ) : (
                      <div className="border rounded overflow-hidden">
                        <div className="grid grid-cols-[2fr_1fr] gap-0 px-3 py-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b">
                          <span>Luna</span>
                          <span className="text-right">Suma plătită</span>
                        </div>
                        {istoric.map(h => (
                          <div key={h.luna} className="grid grid-cols-[2fr_1fr] gap-0 px-3 py-1 border-b last:border-0 text-sm">
                            <span>{formatLuna(h.luna)}</span>
                            <span className="text-right tabular-nums">{formatNum(h.suma)}</span>
                          </div>
                        ))}
                        <div className="grid grid-cols-[2fr_1fr] gap-0 px-3 py-1.5 border-t-2 border-slate-300 bg-slate-50 text-sm font-semibold">
                          <span>Total achitat</span>
                          <span className="text-right tabular-nums">{formatNum(p.total_alocat)}</span>
                        </div>
                        <div className="grid grid-cols-[2fr_1fr] gap-0 px-3 py-1.5 bg-slate-50 text-sm font-semibold">
                          <span>Sold rămas</span>
                          <span className={`text-right tabular-nums ${p.sold_ramas <= 0 ? 'text-green-600' : ''}`}>{formatNum(p.sold_ramas)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? 'Editează poprire' : 'Adaugă poprire nouă'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editId && (
              <div>
                <label className="text-xs font-medium text-slate-600">Angajat *</label>
                <Select value={form.employee_id} onValueChange={handleEmployeeSelect}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selectează angajat" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.nume} {e.prenume}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600">Suma totală de reținut (RON) *</label>
              <Input type="number" step="0.01" className="mt-1" value={form.suma_totala} onChange={e => setForm(f => ({ ...f, suma_totala: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Înființator (ex: BEJ Vasile și Compania) *</label>
              <Input className="mt-1" value={form.infintator} onChange={e => setForm(f => ({ ...f, infintator: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">IBAN terț *</label>
              <Input className="mt-1" value={form.iban_tert} onChange={e => setForm(f => ({ ...f, iban_tert: e.target.value }))} placeholder="RO..." />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Banca beneficiar *</label>
              <Input className="mt-1" value={form.banca_beneficiar} onChange={e => setForm(f => ({ ...f, banca_beneficiar: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">CIF beneficiar</label>
              <Input className="mt-1" value={form.cif_beneficiar} onChange={e => setForm(f => ({ ...f, cif_beneficiar: e.target.value }))} />
            </div>
            <div className="col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Detalii plată (pentru CSV)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Detalii 1</label>
                  <Input className="mt-1" value={form.detalii_1} onChange={e => setForm(f => ({ ...f, detalii_1: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Detalii 2</label>
                  <Input className="mt-1" value={form.detalii_2} onChange={e => setForm(f => ({ ...f, detalii_2: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Detalii 3</label>
                  <Input className="mt-1" value={form.detalii_3} onChange={e => setForm(f => ({ ...f, detalii_3: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Detalii 4</label>
                  <Input className="mt-1" value={form.detalii_4} onChange={e => setForm(f => ({ ...f, detalii_4: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Opțiuni CSV</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Urgent</label>
                  <Input className="mt-1" value={form.urgent} onChange={e => setForm(f => ({ ...f, urgent: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Plata buget</label>
                  <Input className="mt-1" value={form.plata_buget} onChange={e => setForm(f => ({ ...f, plata_buget: e.target.value }))} />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Descriere (opțional)</label>
              <Input className="mt-1" value={form.descriere} onChange={e => setForm(f => ({ ...f, descriere: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
            <Button onClick={handleSave}><Save className="w-4 h-4 mr-1" />{editId ? 'Salvează' : 'Creează'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

// ============ Alocare Lunara ============
const AlocareLunara = memo(function AlocareLunara({ token }) {
  const [month, setMonth] = useState(() => { const m = new Date().getMonth(); return m === 0 ? 12 : m; });
  const [year, setYear] = useState(() => { const m = new Date().getMonth(); return m === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(); });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localAlocari, setLocalAlocari] = useState({});
  const [isSaved, setIsSaved] = useState(false);

  const luna = `${year}-${String(month).padStart(2, '0')}`;

  const fetchAlocare = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/popriri/alocare/${luna}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        // Check if any allocation is already saved
        const anySaved = (d.employees || []).some(emp => emp.popriri.some(p => p.saved));
        setIsSaved(anySaved);
        const local = {};
        (d.employees || []).forEach(emp => {
          emp.popriri.forEach(p => {
            local[`${emp.employee_id}_${p.poprire_id}`] = String(p.suma_alocata || 0);
          });
        });
        setLocalAlocari(local);
      }
    } finally { setLoading(false); }
  }, [luna, token]);

  useEffect(() => { fetchAlocare(); }, [fetchAlocare]);

  const updateAlloc = (empId, poprireId, value) => {
    setLocalAlocari(prev => ({ ...prev, [`${empId}_${poprireId}`]: value }));
    setIsSaved(false); // Mark as unsaved when user edits
  };

  const handleSave = async () => {
    if (!data?.employees?.length) return;
    setSaving(true);
    try {
      const alocari = data.employees.map(emp => ({
        employee_id: emp.employee_id,
        employee_name: emp.employee_name,
        popriri: emp.popriri.map(p => ({
          poprire_id: p.poprire_id,
          suma: parseFloat(localAlocari[`${emp.employee_id}_${p.poprire_id}`] || 0)
        }))
      }));
      const res = await fetch(`${API_URL}/api/popriri/alocare/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ luna, alocari })
      });
      if (res.ok) {
        toast.success(`Alocare salvată pentru ${MONTHS_RO[month-1]?.label} ${year}`);
        setIsSaved(true);
        fetchAlocare();
      }
      else { const e = await res.json(); toast.error(e.detail || 'Eroare'); }
    } catch { toast.error('Eroare de rețea'); }
    finally { setSaving(false); }
  };

  const employees = data?.employees || [];

  return (
    <div className="space-y-4" data-testid="alocare-lunara">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <LunaNav month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
        {employees.length > 0 && (
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-alocare-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Salvează alocarea
          </Button>
        )}
      </div>

      {/* Status banner */}
      {!loading && employees.length > 0 && (
        isSaved ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg" data-testid="alocare-saved-banner">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700 font-medium">Alocarea pentru {MONTHS_RO[month-1]?.label} {year} este salvată</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="alocare-unsaved-banner">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700 font-medium">Alocarea pentru {MONTHS_RO[month-1]?.label} {year} nu este salvată</span>
          </div>
        )
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-slate-50">
          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Nu există angajați cu rețineri pentru {MONTHS_RO[month-1]?.label} {year}</p>
          <p className="text-xs text-slate-400 mt-1">Încărcați un stat de plată din Adaptare pentru această lună</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_2fr_1fr] gap-0 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b bg-slate-50">
            <span>Angajat</span>
            <span className="text-right">Rețineri din stat</span>
            <span className="text-center">Alocare pe popriri</span>
            <span className="text-right">Total alocat</span>
          </div>
          {employees.map((emp, idx) => {
            const totalAlocat = emp.popriri.reduce((s, p) => s + parseFloat(localAlocari[`${emp.employee_id}_${p.poprire_id}`] || 0), 0);
            const diff = Math.abs(emp.retineri_stat - totalAlocat);
            const isMatch = diff < 0.01;

            return (
              <div key={emp.employee_id} className="grid grid-cols-[2fr_1fr_2fr_1fr] gap-0 px-3 py-2 items-start border-b last:border-0" data-testid={`alocare-row-${idx}`}>
                <div>
                  <p className="text-sm font-medium">{emp.employee_name}</p>
                  {!emp.has_popriri && (
                    <p className="text-[10px] text-orange-500 flex items-center gap-1 mt-0.5">
                      <AlertTriangle className="w-3 h-3" />Fără poprire definită
                    </p>
                  )}
                </div>
                <span className="text-sm font-semibold text-right tabular-nums">{formatNum(emp.retineri_stat)}</span>
                <div className="space-y-1 px-2">
                  {emp.popriri.map(p => (
                    <div key={p.poprire_id} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 truncate flex-1" title={p.infintator}>{p.infintator}</span>
                      <Input
                        type="number" step="0.01"
                        className="w-24 h-7 text-xs text-right"
                        value={localAlocari[`${emp.employee_id}_${p.poprire_id}`] || ''}
                        onChange={e => updateAlloc(emp.employee_id, p.poprire_id, e.target.value)}
                      />
                    </div>
                  ))}
                  {emp.popriri.length === 0 && <span className="text-xs text-slate-400">-</span>}
                </div>
                <div className="text-right">
                  <span className={`text-sm font-semibold tabular-nums ${isMatch ? 'text-green-600' : 'text-red-600'}`}>{formatNum(totalAlocat)}</span>
                  {!isMatch && <p className="text-[10px] text-red-500">diferență: {formatNum(diff)}</p>}
                  {isMatch && <CheckCircle2 className="w-3 h-3 text-green-500 inline ml-1" />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ============ Export Popriri (CSV for bank + XLS for tracking) ============

const SETTINGS_DEFAULTS = {
  banca_platitor: '', iban_platitor: '', nume_platitor: '', cod_fiscal_platitor: '',
  numar_op: '', data: '', valuta: ''
};

const SETTINGS_FIELDS_UI = [
  { key: 'banca_platitor', label: 'Banca platitor' },
  { key: 'iban_platitor', label: 'IBAN Platitor' },
  { key: 'nume_platitor', label: 'Nume platitor' },
  { key: 'cod_fiscal_platitor', label: 'Cod fiscal platitor' },
  { key: 'numar_op', label: 'Numar OP' },
  { key: 'data', label: 'Data (DD.MM.YYYY)', placeholder: '15.03.2026' },
  { key: 'valuta', label: 'Valuta', placeholder: 'RON' },
];

const ExportPopriri = memo(function ExportPopriri({ token }) {
  const [month, setMonth] = useState(() => { const m = new Date().getMonth(); return m === 0 ? 12 : m; });
  const [year, setYear] = useState(() => { const m = new Date().getMonth(); return m === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(); });
  const [exporting, setExporting] = useState(false);
  const [exportingXls, setExportingXls] = useState(false);
  const [settings, setSettings] = useState({ ...SETTINGS_DEFAULTS });
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  const luna = `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/popriri/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setSettings(s => ({ ...SETTINGS_DEFAULTS, ...data }));
        }
      } catch {}
    };
    loadSettings();
  }, [token]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/popriri/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (res.ok) toast.success('Setări salvate');
      else toast.error('Eroare la salvare');
    } finally { setSaving(false); }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/popriri/export-csv/${luna}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `POPRIRI_${luna}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV exportat');
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Eroare export');
      }
    } catch { toast.error('Eroare de rețea'); }
    finally { setExporting(false); }
  };

  const handleExportXLS = async () => {
    setExportingXls(true);
    try {
      const res = await fetch(`${API_URL}/api/popriri/export-xls/${luna}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `POPRIRE CRIMACO ${luna}.xls`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('XLS exportat');
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Eroare export');
      }
    } catch { toast.error('Eroare de rețea'); }
    finally { setExportingXls(false); }
  };

  return (
    <div className="space-y-4" data-testid="export-popriri">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm flex items-center gap-2"><Download className="w-4 h-4" />Export Popriri</CardTitle>
            <LunaNav month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 border rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">CSV pentru import bancar</h4>
              <button onClick={() => setShowSettings(!showSettings)} className="text-xs text-blue-600 hover:underline" data-testid="toggle-csv-settings">
                {showSettings ? 'Ascunde setări' : 'Setări CSV'}
              </button>
            </div>
            {showSettings && (
              <div className="mt-2 space-y-3 p-3 bg-slate-50 rounded border">
                <div className="grid grid-cols-2 gap-2">
                  {SETTINGS_FIELDS_UI.map(f => (
                    <div key={f.key}>
                      <label className="text-xs text-slate-600 font-medium">{f.label}</label>
                      <Input
                        className="mt-0.5 h-7 text-xs"
                        value={settings[f.key] || ''}
                        onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                        placeholder={f.placeholder || ''}
                        data-testid={`csv-field-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={handleSaveSettings} disabled={saving} data-testid="save-csv-settings-btn">
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Salvează setări
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-500">Format tabelar cu 18 coloane (Banca platitor, IBAN, Suma, etc.) compatibil cu aplicația băncii.</p>
            <Button onClick={handleExportCSV} disabled={exporting} data-testid="export-csv-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Descarcă CSV
            </Button>
          </div>
          <div className="p-3 border rounded-lg space-y-2">
            <h4 className="text-sm font-semibold">XLS evidență popriri</h4>
            <p className="text-xs text-slate-500">Fișier Excel cu câte un sheet per poprire, incluzând istoricul plăților și soldul rămas.</p>
            <Button variant="outline" onClick={handleExportXLS} disabled={exportingXls} data-testid="export-xls-btn">
              {exportingXls ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Descarcă XLS
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

// ============ Main Tab ============
const SUB_TABS = [
  { id: 'gestiune', label: 'Gestiune Popriri', icon: FileText },
  { id: 'alocare', label: 'Alocare Lunară', icon: Users },
  { id: 'export', label: 'Export', icon: Download },
];

const PopririTab = memo(function PopririTab({ token }) {
  const [activeTab, setActiveTab] = useState('gestiune');

  return (
    <div className="space-y-4" data-testid="popriri-tab">
      <div className="flex border-b overflow-x-auto">
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`popriri-subtab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'gestiune' && <GestiunePopriri token={token} />}
      {activeTab === 'alocare' && <AlocareLunara token={token} />}
      {activeTab === 'export' && <ExportPopriri token={token} />}
    </div>
  );
});

export default PopririTab;
